export interface RuntimeConfig {
  VITE_APP_VERSION: string;
  VITE_API_BASE_URL: string;
  VITE_INTEGRATION_CONFIG: string;
}

let runtimeConfig: Readonly<RuntimeConfig> | undefined;

export function getBootstrapBaseUrl(): string {
  // Only used to locate the configuration endpoint. All application settings
  // (including the API URL used afterward) must still load from the database.
  const baseUrl = (window as any).__RUNTIME_CONFIG__?.VITE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL;
  if (typeof baseUrl !== 'string' || !baseUrl.trim()) {
    throw new Error('Missing bootstrap VITE_API_BASE_URL');
  }
  return httpUrl(baseUrl.trim(), 'bootstrap VITE_API_BASE_URL').replace(/\/+$/, '');
}

function httpUrl(value: string, key: string): string {
  const url = new URL(value);
  if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) {
    throw new Error(`Invalid URL for ${key}`);
  }
  return value;
}

export function parseRuntimeConfig(payload: unknown): Readonly<RuntimeConfig> {
  if (!payload || typeof payload !== 'object' || !('configurations' in payload)
    || !Array.isArray(payload.configurations)) {
    throw new Error('Invalid configuration response');
  }

  const values = new Map<string, string>();
  for (const row of payload.configurations) {
    if (!row || typeof row.config_key !== 'string' || typeof row.config_value !== 'string') {
      throw new Error('Invalid configuration record');
    }
    if (values.has(row.config_key)) throw new Error(`Duplicate configuration: ${row.config_key}`);
    values.set(row.config_key, row.config_value.trim());
  }

  const required = (key: keyof RuntimeConfig): string => {
    const value = values.get(key);
    if (!value) throw new Error(`Missing configuration: ${key}`);
    return value;
  };

  return Object.freeze({
    VITE_APP_VERSION: required('VITE_APP_VERSION'),
    VITE_API_BASE_URL: httpUrl(required('VITE_API_BASE_URL'), 'VITE_API_BASE_URL').replace(/\/+$/, ''),
    VITE_INTEGRATION_CONFIG: httpUrl(required('VITE_INTEGRATION_CONFIG'), 'VITE_INTEGRATION_CONFIG'),
  });
}

export async function loadRuntimeConfig(): Promise<Readonly<RuntimeConfig>> {
  const baseUrl = getBootstrapBaseUrl();
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(`${baseUrl}/api/environment-configurations`, {
      cache: 'no-store',
      credentials: 'omit',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });
    if (!response.ok) throw new Error(`Configuration request failed (${response.status})`);
    runtimeConfig = parseRuntimeConfig(await response.json());
    return runtimeConfig;
  } finally {
    clearTimeout(timeout);
  }
}

export function getRuntimeConfig(): Readonly<RuntimeConfig> {
  if (!runtimeConfig) throw new Error('Runtime configuration has not loaded');
  return runtimeConfig;
}
